/**
 * A component that toggles between truncated and full text while preserving HTML formatting
 */
class TextShowMore {
  constructor(/** @type {HTMLElement} */ element) {
    this.el = element;
    this.content = /** @type {HTMLElement | null} */ (element.querySelector('[data-text-content]'));
    this.button = /** @type {HTMLElement | null} */ (element.querySelector('[data-show-more-button]'));
    this.truncateWords = parseInt(this.el.dataset.truncateWords || '50', 10);
    this.fullHtml = '';
    this.truncatedHtml = '';

    this.init();
  }

  init() {
    if (!this.content || !this.button) return;

    // Clean up empty HTML tags from the original content
    this.fullHtml = this.cleanEmptyTags(this.content.innerHTML.trim());
    const wordCount = (this.content.textContent?.trim().match(/\S+/g) || []).length;

    if (wordCount <= this.truncateWords) {
      this.button.style.display = 'none';
      return;
    }

    this.truncatedHtml = this.createTruncatedHtml();
    this.content.innerHTML = this.truncatedHtml;
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });
  }

  // Remove empty paragraph tags that can appear from RTE content or Range API fragments
  cleanEmptyTags(/** @type {string} */ html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove empty <p> tags (with only whitespace or nothing)
    const emptyParagraphs = temp.querySelectorAll('p');
    emptyParagraphs.forEach(p => {
      if (!p.textContent || p.textContent.trim() === '') {
        p.remove();
      }
    });
    
    return temp.innerHTML;
  }

  // Preserve the HTML formatting of the truncated text
  createTruncatedHtml() {
    if (!this.content) return this.fullHtml;

    const walker = document.createTreeWalker(
      this.content,
      NodeFilter.SHOW_TEXT,
      null
    );

    let wordCount = 0;
    let targetNode = null;
    let targetOffset = 0;

    // Find the truncation point by counting words across text nodes
    while (walker.nextNode() && wordCount < this.truncateWords) {
      const node = walker.currentNode;
      const text = node.textContent || '';
      const words = text.match(/\S+/g) || [];

      if (wordCount + words.length >= this.truncateWords) {
        targetNode = node;
        const remaining = this.truncateWords - wordCount;

        // Find character position after the last word we want to include
        const wordMatches = text.matchAll(/\S+/g);
        let wordIdx = 0;
        let lastMatchEnd = 0;

        for (const match of wordMatches) {
          wordIdx++;
          if (wordIdx <= remaining && match.index !== undefined) {
            lastMatchEnd = match.index + match[0].length;
          }
          if (wordIdx >= remaining) break;
        }

        targetOffset = lastMatchEnd;
        break;
      }
      wordCount += words.length;
    }

    if (!targetNode) return this.fullHtml;

    const range = document.createRange();
    range.setStart(this.content, 0);
    range.setEnd(targetNode, targetOffset);
    const fragment = range.cloneContents();
    const div = document.createElement('div');
    div.appendChild(fragment);
    
    // Clean up empty tags from the truncated HTML as well
    return this.cleanEmptyTags(div.innerHTML + '...');
  }

  toggle() {
    if (!this.content || !this.button) return;

    const isExpanded = this.el.dataset.expanded === 'true';
    const newState = !isExpanded;

    this.content.innerHTML = newState ? this.fullHtml : this.truncatedHtml;
    this.el.dataset.expanded = newState.toString();
    this.button.ariaExpanded = newState.toString();
  }
}

const initShowMore = (/** @type {Element | Document} */ container = document) =>
  container.querySelectorAll('[data-text-show-more]').forEach(el => new TextShowMore(/** @type {HTMLElement} */(el)));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initShowMore());
} else {
  initShowMore();
}

// Keep everything in sync when updates are made in the theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initShowMore(event.target);
  }
});
